import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';

interface SecurityValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface RateLimitConfig {
  endpoint: string;
  maxRequests: number;
  windowMinutes: number;
}

export const useSecurityValidation = () => {
  const { user } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  const [lastValidationTime, setLastValidationTime] = useState<number>(0);

  // Rate limiting validation
  const checkRateLimit = useCallback(async (config: RateLimitConfig): Promise<boolean> => {
    if (!user) return false;
    
    try {
      const { data, error } = await supabase.rpc('check_rate_limit', {
        _endpoint: config.endpoint,
        _max_requests: config.maxRequests,
        _window_minutes: config.windowMinutes
      });
      
      if (error) {
        console.error('Rate limit check failed:', error);
        return false;
      }
      
      return data;
    } catch (error) {
      console.error('Rate limit validation error:', error);
      return false;
    }
  }, [user]);

  // Server-side permission validation
  const validateServerPermission = useCallback(async (requiredRole: 'user' | 'admin' | 'superadmin' = 'user'): Promise<boolean> => {
    if (!user || roleLoading) return false;
    
    try {
      const { data, error } = await supabase.rpc('user_has_permission', {
        _user_id: user.id,
        _required_role: requiredRole
      });
      
      if (error) {
        console.error('Permission validation failed:', error);
        await logSecurityEvent('permission_validation_failed', 'user_permission', user.id, false, error.message);
        return false;
      }
      
      return data;
    } catch (error) {
      console.error('Permission validation error:', error);
      return false;
    }
  }, [user, roleLoading]);

  // Log security events
  const logSecurityEvent = useCallback(async (
    action: string,
    resourceType?: string,
    resourceId?: string,
    success: boolean = true,
    errorMessage?: string
  ): Promise<void> => {
    try {
      await supabase.rpc('log_security_event', {
        _action: action,
        _resource_type: resourceType,
        _resource_id: resourceId,
        _success: success,
        _error_message: errorMessage
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }, []);

  // Validate user data integrity (prevent client-side manipulation)
  const validateUserDataIntegrity = useCallback(async (): Promise<SecurityValidationResult> => {
    const result: SecurityValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    if (!user) {
      result.isValid = false;
      result.errors.push('User not authenticated');
      return result;
    }

    // Skip validation if role is still loading
    if (roleLoading) {
      return result;
    }

    try {
      // Validate user role matches server-side
      const { data: serverRole, error } = await supabase.rpc('get_user_role', {
        _user_id: user.id
      });

      if (error) {
        result.errors.push('Failed to validate user role');
        result.isValid = false;
      } else if (serverRole && role && serverRole !== role) {
        // Only report mismatch if both roles are defined and different
        console.warn(`Role mismatch: client=${role}, server=${serverRole}`);
        result.warnings.push(`Client-side role mismatch detected (client: ${role}, server: ${serverRole})`);
        await logSecurityEvent('role_mismatch_detected', 'user_role', user.id, false, `client=${role}, server=${serverRole}`);
      }

      // Check for session validity
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        result.errors.push('Invalid session detected');
        result.isValid = false;
      }

    } catch (error) {
      result.errors.push('Data integrity validation failed');
      result.isValid = false;
    }

    return result;
  }, [user, role, roleLoading]);

  // Validate critical operations
  const validateCriticalOperation = useCallback(async (
    operation: string,
    requiredRole: 'user' | 'admin' | 'superadmin' = 'user',
    rateLimit?: RateLimitConfig
  ): Promise<SecurityValidationResult> => {
    const result: SecurityValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // 1. Check authentication
    if (!user) {
      result.isValid = false;
      result.errors.push('Authentication required');
      return result;
    }

    // 2. Validate data integrity
    const integrityCheck = await validateUserDataIntegrity();
    if (!integrityCheck.isValid) {
      result.isValid = false;
      result.errors.push(...integrityCheck.errors);
      return result;
    }

    // 3. Check server-side permissions
    const hasPermission = await validateServerPermission(requiredRole);
    if (!hasPermission) {
      result.isValid = false;
      result.errors.push('Insufficient permissions');
      await logSecurityEvent(`unauthorized_${operation}`, 'operation', operation, false);
      return result;
    }

    // 4. Check rate limits if configured
    if (rateLimit) {
      const rateLimitOk = await checkRateLimit(rateLimit);
      if (!rateLimitOk) {
        result.isValid = false;
        result.errors.push('Rate limit exceeded');
        return result;
      }
    }

    // 5. Log successful validation
    await logSecurityEvent(`validated_${operation}`, 'operation', operation, true);

    return result;
  }, [user, validateUserDataIntegrity, validateServerPermission, checkRateLimit, logSecurityEvent]);

  // Periodic security validation
  useEffect(() => {
    if (!user || roleLoading) return;

    const validatePeriodically = async () => {
      const now = Date.now();
      
      // Run validation every 5 minutes
      if (now - lastValidationTime > 5 * 60 * 1000) {
        const validation = await validateUserDataIntegrity();
        
        if (!validation.isValid) {
          console.warn('Security validation failed:', validation.errors);
          // Force re-authentication if critical issues found (only for errors, not warnings)
          if (validation.errors.some(e => e.includes('Invalid session'))) {
            window.location.href = '/admin/auth';
          }
        }
        
        setLastValidationTime(now);
      }
    };

    const interval = setInterval(validatePeriodically, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [user, roleLoading, lastValidationTime, validateUserDataIntegrity]);

  return {
    validateCriticalOperation,
    validateServerPermission,
    validateUserDataIntegrity,
    checkRateLimit,
    logSecurityEvent
  };
};